import { type Ref } from 'react';

import { type CustomContentProps, SnackbarContent, useSnackbar } from 'notistack';

import Alert, { type AlertProps } from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import { mergeSx } from '../../util/mui/sx';

interface SnackbarAlertProps extends CustomContentProps {
  readonly alertProps?: AlertProps;
  /* eslint-disable-next-line react/boolean-prop-naming -- Established public prop name. */
  readonly dismissable?: boolean;
  readonly ref?: Ref<HTMLDivElement>;
  readonly title?: string;
}

export const SnackbarAlert = ({
  action,
  alertProps,
  className,
  dismissable = true,
  id,
  message,
  ref,
  style,
  title,
  variant,
}: SnackbarAlertProps) => {
  const { closeSnackbar } = useSnackbar();

  return (
    <Alert
      {...alertProps}
      action={action && typeof action === 'function' ? action(id) : action}
      className={className}
      component={SnackbarContent}
      onClose={action || !dismissable ? undefined : () => closeSnackbar(id)}
      ref={ref}
      role='alert'
      severity={variant === 'default' ? undefined : variant}
      style={style}
      sx={mergeSx(
        {
          alignItems: title ? undefined : 'center',
          boxShadow: [
            '0px 3px 5px -1px rgba(0,0,0,0.2)',
            '0px 6px 10px 0px rgba(0,0,0,0.14)',
            '0px 1px 18px 0px rgba(0,0,0,0.12)',
          ].join(','),
          div: { padding: 0 },
        },
        alertProps?.sx,
      )}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
    </Alert>
  );
};
